
import { useState } from 'react';

export interface ServiceFormData {
  service_name: string;
  description: string;
  session_instructions?: string;
  price: string;
  duration_minutes: number;
  duration_hours: number;
  duration_minutes_part: number;
  therapy_tags: string[];
  custom_therapy_tags: string[];
  formats_available: string[];
  currency: string;
  is_active: boolean;
  is_free_session: boolean;
  parental_consent_required?: boolean;
  adults_only?: boolean;
  service_track: 'clinical' | 'wellness';
}

export interface ServiceFormState {
  newService: ServiceFormData;
  selectedFormats: Record<string, boolean>;
  setNewService: React.Dispatch<React.SetStateAction<ServiceFormData>>;
  setSelectedFormats: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  resetForm: () => void;
  handleFormatChange: (format: string, checked: boolean) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (name: string, value: any) => void;
  handleToggleActive: (value: boolean) => void;
  handleToggleFreeSession: (value: boolean) => void;
  handleToggleParentalConsent: (value: boolean) => void;
  handleToggleAdultsOnly: (value: boolean) => void;
  handleServiceTrackChange: (value: 'clinical' | 'wellness') => void;
}

const initialFormData: ServiceFormData = {
  service_name: '',
  description: '',
  session_instructions: '',
  price: '',
  duration_minutes: 60,
  duration_hours: 1,
  duration_minutes_part: 0,
  therapy_tags: [],
  custom_therapy_tags: [],
  formats_available: ['virtual'],
  currency: 'USD',
  is_active: true,
  is_free_session: false,
  parental_consent_required: false,
  adults_only: false,
  service_track: 'wellness',
};

const initialFormats = {
  in_person: false,
  virtual: true,
};

export const useServiceFormState = (): ServiceFormState => {
  const [newService, setNewService] = useState<ServiceFormData>(initialFormData);
  const [selectedFormats, setSelectedFormats] = useState<Record<string, boolean>>(initialFormats);

  const resetForm = () => {
    setNewService(initialFormData);
    setSelectedFormats(initialFormats);
  };

  const handleFormatChange = (format: string, checked: boolean) => {
    setSelectedFormats(prev => ({
      ...prev,
      [format]: checked,
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewService(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name: string, value: any) => {
    setNewService(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleActive = (value: boolean) => {
    setNewService(prev => ({
      ...prev,
      is_active: value,
    }));
  };

  const handleToggleFreeSession = (value: boolean) => {
    setNewService(prev => ({
      ...prev,
      is_free_session: value,
    }));
  };

  const handleToggleParentalConsent = (value: boolean) => {
    setNewService(prev => ({
      ...prev,
      parental_consent_required: value,
    }));
  };

  const handleToggleAdultsOnly = (value: boolean) => {
    setNewService(prev => ({
      ...prev,
      adults_only: value,
    }));
  };

  const handleServiceTrackChange = (value: 'clinical' | 'wellness') => {
    setNewService(prev => ({
      ...prev,
      service_track: value,
    }));
  };

  return {
    newService,
    selectedFormats,
    setNewService,
    setSelectedFormats,
    resetForm,
    handleFormatChange,
    handleInputChange,
    handleSelectChange,
    handleToggleActive,
    handleToggleFreeSession,
    handleToggleParentalConsent,
    handleToggleAdultsOnly,
    handleServiceTrackChange,
  };
};
